import React, { forwardRef } from 'react';
import { css, cx } from '@emotion/css';
import { theme, expandFontTheme, IconExternalLink, useThemeContext, getPrefixedThemeColor, } from '@contrast-security-inc/design-system-foundations';
export const validLinkColors = [
    'neutral70',
    'neutral100',
    'red50',
    'turquoise90',
    'darkTheme.teal100',
    'darkTheme.red100',
];
export const validFonts = [
    'body',
    'labelSm',
    'metadata',
    'darkTheme.body',
    'darkTheme.label',
    'darkTheme.assistive',
];
const linkColor = (isDisabled, selectedTheme, color) => {
    const themedColor = getPrefixedThemeColor(color);
    const disabledColor = selectedTheme === 'dark' ? theme.color.neutral60 : theme.color.neutral70;
    return isDisabled ? disabledColor : themedColor;
};
const linkStyles = (color, isDisabled, selectedTheme, font = '') => css `
  ${selectedTheme === 'dark' ? expandFontTheme(font || 'darkTheme.body') : expandFontTheme(font)}
  color: ${linkColor(isDisabled, selectedTheme, color)};
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  &:hover {
    text-decoration: ${isDisabled ? 'none' : 'underline'};
    color: ${isDisabled ? theme.color.neutral70 : theme.color[color]};
  }

  &:focus-visible {
    border-radius: 2px;
    outline: 1px solid ${theme.color.turquoise90};
    outline-offset: ${theme.spacing.px4};
    text-decoration: underline;
  }

  &:link,
  &:visited,
  &:hover,
  &:focus {
    color: ${linkColor(isDisabled, selectedTheme, color)};
  }
`;
const externalLinkIconStyles = css `
  margin-left: ${theme.spacing.px4};
`;
export const Link = forwardRef(({ className, isExternal, testId, color, font, children, onClick, isDisabled, href, role = 'link', hideExternalIcon, ...rest }, ref) => {
    if (color && !validLinkColors.includes(color)) {
        throw new Error(`Invalid link color, valid colors are ${validLinkColors} and you passed in ${color}.`);
    }
    const { selectedTheme } = useThemeContext();
    const themedColor = selectedTheme === 'dark' ? 'darkTheme.teal100' : 'turquoise90';
    const classes = cx(linkStyles(color ?? themedColor, isDisabled, selectedTheme, font), className, 'ds-link');
    return (React.createElement("a", { className: classes, ref: ref, target: isExternal ? '_blank' : undefined, rel: isExternal ? 'noopener noreferrer' : undefined, "data-testid": testId, role: role, ...(href && !isDisabled && { href }), ...(onClick && !isDisabled && { onClick }), ...rest },
        children,
        isExternal && !hideExternalIcon ? (React.createElement(IconExternalLink, { className: externalLinkIconStyles, size: "sm", color: isDisabled ? 'neutral70' : themedColor })) : null));
});
Link.displayName = 'Link';
